/**
 * The pricing controls and totals (prototype `vQuote`'s right column).
 *
 * Two different numbers live here and the distinction matters:
 *
 *   PREVIEW — recomputed on the client as the purchaser edits, so the totals move with the
 *             typing (contract §3: "currency/commission/convenience recompute is FE").
 *   SAVED   — what the server returned. Shown once the preview agrees, and the only number
 *             that goes to the customer.
 *
 * Sending is blocked here when rules are violated and enforcement is on — but the block that
 * counts is the server's 409; this only saves the user a round-trip.
 */

import { Badge } from '../../components/Badge'
import { formatMoney } from '../../lib/format'
import { Money } from '../../components/Money'
import { Button, Card } from '../../components/ui'
import { fxLookup, type WireFx } from '../../lib/api/wire'
import { companyPoCost, marginAfterCommission, totalQuantity, convertTotal } from '../../lib/quotationTotals'
import { computeQuotationTotals, type DiscountInput, type TaxRow } from '../../lib/salesTaxes'
import { convert } from '../../lib/fx'
import type { Currency, Money as MoneyValue, QuotationLine, QuotationTotals } from '../../lib/types'

export function TotalsPanel({
  totals,
  displayCurrency,
  currencies,
  fx,
  blocked,
  sending,
  status,
  rateNote,
  marginNote,
  commissionPct = 0,
  recipients,
  recipientsPending,
  onChangeCurrency,
  onSend,
  onDownload,
  downloading = false,
  onDownloadPdf,
  downloadingPdf = false,
  downloadBlockedReason = null,
  onMarkSent,
  markingSent = false,
  unpricedWarning = null,
  quotationCurrency,
  companyCurrency,
  exchangeRate,
  lines,
  taxes,
  discount,
  onApplyCurrency,
  applyingCurrency,
  sendLabel,
  sendBlockedReason,
}: {
  totals: QuotationTotals
  displayCurrency: Currency
  /** The send button's wording — the host's, since a revision is not worded like a first send. */
  /**
   * Whether the three terms may be edited. The HOST decides, because the answer differs by
   * screen: `/quote` locks a sent quotation (altering one under a customer silently is not an
   * edit, it is a discrepancy), while `/revisions` exists precisely to change a sent quotation
   * and unlocks them.
   *
   * The server permits either — measured 2026-08-09, `update_quotation` accepted eight
   * consecutive markup writes to a `Quoted` document — so this is our rule, not its.
   */
  /** The currency the DOCUMENT is written in — distinct from the one being viewed. */
  quotationCurrency: Currency
  /** The company's currency and the document's company → quotation rate — the PO cost is shown in the former. */
  companyCurrency: Currency
  exchangeRate: number
  /**
   * Applies the viewed currency to the document. `null` when there is nothing to apply (already
   * viewing the document's own currency) or when no rate is on file for the pair — the caller
   * decides, because it holds the FX table.
   */
  onApplyCurrency: (() => void) | null
  applyingCurrency: boolean
  sendLabel: string
  /**
   * A host-imposed reason the send is blocked, or `null`. Distinct from this panel's OWN gates
   * (violations, non-draft, no recipient) because the host knows things the panel cannot — on
   * `/revisions`, that unresolved customer requests must be dealt with first.
   */
  sendBlockedReason: string | null
  /**
   * What the picker offers. Comes from the caller — the currencies we hold a rate for, plus the
   * quotation's own.
   *
   * This was a hardcoded `['AED','USD','EUR']`, which meant a quotation written in SAR could not
   * be shown in SAR: the `<select>` had no matching option, so it rendered on AED while the page
   * was priced in something else. The bench quotes customers in SAR, BHD and QAR today.
   */
  currencies: readonly Currency[]
  fx: WireFx
  /** The quotation's lines — for the total quantity and PO cost on the strip below. */
  lines: readonly QuotationLine[]
  /** The tax rows and the discount, so this panel's grand total is the grid's grand total. */
  taxes: readonly TaxRow[]
  discount: DiscountInput | null
  blocked: boolean
  sending: boolean
  status: string
  onChangeCurrency: (currency: Currency) => void
  onSend: () => void
  /** Builds and downloads the customer workbook. Absent hides the control. */
  onDownload?: () => void
  /**
   * "Sent outside the app" — the sales team mailed this quotation themselves (Akhil,
   * 2026-09-18). Marks it sent without sending; offered on a draft only, under the real send.
   */
  onMarkSent?: () => void
  markingSent?: boolean
  /** Lines going out at 0.00 — shown, never blocking. See `describeUnpriced`. */
  unpricedWarning?: string | null
  downloading?: boolean
  /** Downloads the customer quotation as the server renders it, as a PDF. Absent hides it. */
  onDownloadPdf?: () => void
  downloadingPdf?: boolean
  /**
   * Why the workbook cannot be downloaded yet — the SAME margin gate the send has (Akhil,
   * 2026-09-19: "don't allow the download unless the margin is added"). A workbook of at-cost or
   * below-cost lines leaves the building the moment it is downloaded, send button or not.
   */
  downloadBlockedReason?: string | null
  /**
   * What the figures are being converted at, and how old that rate is — `null` when the picker
   * is on the quotation's own currency and nothing is being converted.
   *
   * Shown right under the picker because that is where the question is asked. The rates on this
   * bench run to years old, and a converted total with no visible date invites nobody to
   * question it.
   */
  rateNote: { text: string; stale: boolean; missing: boolean } | null
  /**
   * How the overall margin comes about when it is not the markup alone (Akhil, 2026-09-17:
   * "markup 30% … the margin changes to 44%"). With a line commission the price is grossed up —
   * cost × (1 + markup) ÷ (1 − commission) — so 30% markup and 10% commission IS 44.4% over cost,
   * in every currency. Said under the figure rather than left to look like a conversion error.
   */
  marginNote?: string | null
  /** The line commission grossed into the prices, in percent — for the margin AFTER it. */
  commissionPct?: number
  /**
   * The addresses this will actually be emailed to. EMPTY IS A REAL ANSWER — 1561 customers on
   * this bench have no contact — and it must block the send: an empty `to` is what dropped the
   * server into its broken address lookup and produced `Unknown column
   * 'customer_primary_contact'` (`B8`). Shown, so "who is this going to" is answered before the
   * click rather than by a database error afterwards.
   */
  recipients: readonly string[]
  recipientsPending: boolean
  /** Commits one term. Called on blur, and only when the value actually changed. */
  /**
   * Whether the send-to-customer card below the figures may render at all (fix round, final
   * review: C1). `/revisions` passes `false`: it has its OWN re-issue button, gated on open
   * revision requests and the queued structural batch — guards this panel has no way to know
   * about. Rather than disabling the button here (an inert control that still looks live is its
   * own trap — a purchaser can still find and click it), the whole send section is omitted, so
   * `/revisions` has exactly one place a send can be triggered from.
   */
}) {
  const marginPositive = totals.sell_subtotal.amount >= totals.cost_base.amount
  /*
    The figures Akhil marked on the ERP totals strip: total line quantity, the totals in BOTH
    currencies when the customer's differs from the company's, and what the goods cost us.
    Formulas in `lib/quotationTotals.ts`, written down so they cannot drift.
  */
  const lineQty = totalQuantity(lines)
  // In the COMPANY's currency whatever the bill is in (Akhil, 2026-09-16) — see `companyPoCost`.
  const poCost = companyPoCost(lines, exchangeRate)
  // `null` when no rate is on file — a second currency shown at par looks correct and is wrong,
  // which is exactly the `$50 = AED 50` failure the comparison shipped with.
  /*
    Taxes and the discount, run through the same engine the grid uses so the two agree by
    construction rather than by both being maintained in step.
  */
  const charges = computeQuotationTotals({
    netTotal: totals.total.amount,
    totalQty: lineQty,
    taxes,
    discount,
  })
  const discountBasis = discount?.applyOn ?? 'Grand Total'
  /*
    The engine works in the QUOTATION's currency (its inputs — line totals, tax amounts, a fixed
    discount — are). Everything it returns is converted for the view here, the same way the
    subtotal above it is. ENQ-2026-00146 (2026-09-16): the subtotal read $4,323.93 while taxes,
    discount and the grand total below it were the AED figures with a $ sign — "$17,717.50" for a
    quotation worth $4,854 — because these three were formatted in the display currency without
    being converted into it. A relabelled number is the one thing this panel must never show.
  */
  const inView = (amount: number): MoneyValue => convert({ amount, currency: quotationCurrency as Currency }, displayCurrency, fx)

  const inCustomerCurrency =
    displayCurrency === quotationCurrency
      ? null
      : convertTotal(totals.total.amount, displayCurrency, fxLookup(fx, quotationCurrency as Currency, displayCurrency))
  const noRecipient = !recipientsPending && recipients.length === 0
  const canSend = !blocked && !sending && status === 'draft' && !noRecipient && !recipientsPending && sendBlockedReason === null

  return (
    <div>
      <Card>
        {/*
          "View in", not "Customer currency". This picker CONVERTS THE DISPLAY and writes nothing
          — it was labelled as the document's currency, which is why choosing another one and
          seeing nothing persist read as a broken control rather than a working one.

          The document's own currency is stated beside it, and changing that is a separate,
          explicit act: it re-prices every line and alters what the customer is billed.
        */}
        <label className="fld">
          <span>
            View in <span style={{ color: 'var(--muted)', fontWeight: 400 }}>· quoted in {quotationCurrency}</span>
          </span>
          <select value={displayCurrency} onChange={(e) => onChangeCurrency(e.target.value as Currency)}>
            {currencies.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>

        {onApplyCurrency ? (
          <div style={{ margin: '-2px 0 10px' }}>
            <Button onClick={onApplyCurrency} disabled={applyingCurrency} style={{ width: '100%' }}>
              {applyingCurrency ? 'Re-pricing…' : `Bill this customer in ${displayCurrency}`}
            </Button>
            <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 4 }}>
              Rewrites every selling price. The customer's total changes.
            </div>
          </div>
        ) : null}

        {rateNote ? (
          <div
            style={{
              fontSize: 12,
              marginTop: -4,
              marginBottom: 10,
              color: rateNote.missing || rateNote.stale ? 'var(--warn)' : 'var(--muted)',
            }}
          >
            {rateNote.missing ? '⚠ ' : rateNote.stale ? '⚠ ' : '💱 '}
            {rateNote.text}
          </div>
        ) : null}

        {/*
          ---- Markup, commission and the convenience fee MOVED to the commission panel ----

          They used to live here as three inputs. The Quotation Commission panel above now owns all
          five commission rates, the markup and the discount, so keeping a second Markup % and a
          second "Broker commission %" here meant two controls writing the same two fields, free to
          show different numbers while one of them was mid-edit.

          The convenience fee moved with them rather than being deleted — it is a document-level
          money term like the rest, and it had no equivalent up there.

          The footnote went too: it pointed at Settings for "the default markup for new ones", and
          those Settings fields are now read-only (`D23`).

          What stays here is the currency VIEW picker above, which converts the display and writes
          nothing — a different kind of control entirely.
        */}
      </Card>

      <Card style={{ marginTop: 14 }}>
        {/*
          The figures from the ERP totals strip. Above the cost/margin rows because they describe
          the DOCUMENT — how much is on it, what it costs us — rather than the pricing arithmetic.
        */}
        <div className="kv">
          <span className="lab">Total quantity</span>
          <span className="mono">{lineQty}</span>
        </div>
        {/* `Σ(rate × qty)` — what the goods cost us, in the company's own currency, NOT converted
            into whatever the customer is being billed in. */}
        <div className="kv">
          <span className="lab" title="Every line's cost x quantity — what these goods cost us">
            Total PO cost
          </span>
          <span className="mono">{formatMoney({ amount: poCost, currency: companyCurrency })}</span>
        </div>
        {/* Only when the two genuinely differ, and only when a rate exists — see `convertTotal`. */}
        {inCustomerCurrency ? (
          <div className="kv">
            <span className="lab">Total ({inCustomerCurrency.currency})</span>
            <span className="mono">{formatMoney(inCustomerCurrency)}</span>
          </div>
        ) : null}
        <div style={{ height: 1, background: 'var(--line)', margin: '8px 0' }} />

        {/* No "Cost base" row (Akhil, 2026-09-16): it repeated "Total PO cost" two lines up. */}
        <div className="kv">
          <span className="lab">Selling subtotal</span>
          <span className="mono">
            <Money value={totals.sell_subtotal} in={displayCurrency} fx={fx} />
          </span>
        </div>
        {/*
          No Markup, Commission or Convenience rows.

          They used to be added here on top of the subtotal, mirroring the server's
          `grand_total = net x (1 + markup/100) + convenience + net x commission/100`. Per the
          business rule (Akhil, 2026-09-04) markup and commission are LINE-level: each line already
          sells at `((cost x markup) + cost) / ((100 - commission) / 100)`, so a row here charged
          the customer for them a second time — AED 800.00 of cost quoted at 1,173.30 rather than
          960.00.

          The server still computes that formula (`D27`). It stays inert because this app never
          writes `markup`, the four commission fields or `convenience_amount` as anything but 0 —
          see `CommissionPanel`, which holds the rates in local state for exactly that reason.
        */}
        {/*
          Taxes and the additional discount, from the SAME engine the taxes grid renders — one
          calculation, two places, so the two can never show different grand totals.

          Each is shown only when it is non-zero: a quotation with no tax listing "Taxes 0.00" is
          noise, and a "Discount 0.00" line invites a question about a reduction nobody gave.
        */}
        {charges.totalTax > 0 ? (
          <div className="kv">
            <span className="lab">Taxes and charges</span>
            <span className="mono">{formatMoney(inView(charges.totalTax))}</span>
          </div>
        ) : null}
        {charges.discountAmount > 0 ? (
          <div className="kv">
            <span className="lab">
              Discount <span style={{ color: 'var(--muted)' }}>· on {discountBasis}</span>
            </span>
            <span className="mono" style={{ color: 'var(--ok)' }}>
              −{formatMoney(inView(charges.discountAmount))}
            </span>
          </div>
        ) : null}
        <div className="kv">
          <span className="lab">
            <b>Total ({displayCurrency})</b>
          </span>
          <span className="tot">
            {/* The engine's figure, NOT `totals.total` — that one is pre-tax and pre-discount, and
                showing it here while the taxes grid showed another number was the one thing this
                panel could not be allowed to do. */}
            {formatMoney(inView(charges.grandTotal))}
          </span>
        </div>
        <div className="kv">
          <span className="lab">Overall margin</span>
          <span className="mono" style={{ color: marginPositive ? 'var(--ok)' : 'var(--danger)' }}>
            {totals.cost_base.amount > 0 ? `${totals.margin_pct.toFixed(1)}%` : '—'}
          </span>
        </div>
        {marginNote && totals.cost_base.amount > 0 ? (
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: -4, marginBottom: 6, textAlign: 'right' }}>{marginNote}</div>
        ) : null}
        {/* The margin once the commission is paid out — the markup that was asked for (2026-09-17). */}
        {commissionPct > 0 && totals.cost_base.amount > 0
          ? (() => {
              const after = marginAfterCommission({ sell: totals.sell_subtotal.amount, cost: totals.cost_base.amount, commissionPct })
              return (
                <>
                  <div className="kv">
                    <span className="lab">Commission ({commissionPct}% of selling)</span>
                    <span className="mono">−{formatMoney(inView(after.commission))}</span>
                  </div>
                  <div className="kv">
                    <span className="lab" title="Selling less the commission, over PO cost">
                      Margin after commission
                    </span>
                    <span className="mono" style={{ color: after.keptOverCost >= 0 ? 'var(--ok)' : 'var(--danger)', fontWeight: 700 }}>
                      {formatMoney(inView(after.keptOverCost))} · {after.pct}%
                    </span>
                  </div>
                </>
              )
            })()
          : null}

        {status !== 'draft' ? (
          <div style={{ marginTop: 10 }}>
            <Badge tone={status === 'Won' ? 'ok' : status === 'Rejected' ? 'danger' : 'info'}>{status}</Badge>
          </div>
        ) : null}

        <>
            <div
              style={{
                fontSize: 12,
                marginTop: 12,
                color: noRecipient ? 'var(--warn)' : 'var(--muted)',
              }}
            >
              {recipientsPending
                ? 'Looking up the customer\u2019s contact\u2026'
                : noRecipient
                  ? '\u26a0 No email address on file for this customer \u2014 add a contact before sending.'
                  : `\u2709 To: ${recipients.join(', ')}`}
            </div>

            <Button variant="primary" style={{ width: '100%', marginTop: 8 }} disabled={!canSend} onClick={onSend}>
              {blocked
                ? '\u26d4 Fix violations to send'
                : /* A disabled button wearing its normal label reads as a broken button. The
                     specific reason is spelled out directly underneath. */
                  sendBlockedReason !== null
                  ? '\u26d4 Can\u2019t send yet'
                  : sending
                  ? 'Sending\u2026'
                  : status !== 'draft'
                    ? `Already ${status}`
                    : noRecipient
                      ? 'No address to send to'
                      : sendLabel}
            </Button>
            {/*
              The workbook the customer is meant to receive, downloadable here.

              It exists because `send_quotation` attaches NOTHING (`B9`: every quotation email on
              this bench carries `attachments: "[]"`, while RFQ mails carry their xlsx). Until that
              is fixed the purchaser can at least take the file and attach it themselves — and
              seeing it is also the only way to check what the customer would get.
            */}
            {onDownload || onDownloadPdf ? (
              <>
                {/* Side by side: the workbook and the PDF are the same quotation in two forms, behind
                    the same margin gate (2026-10-28). */}
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  {onDownload ? (
                    <Button
                      style={{ flex: 1 }}
                      disabled={downloading || downloadBlockedReason !== null}
                      title={downloadBlockedReason ?? 'The customer workbook'}
                      onClick={onDownload}
                    >
                      {downloading ? 'Building…' : downloadBlockedReason !== null ? '⛔ Add margin first' : '⬇ Excel (.xlsx)'}
                    </Button>
                  ) : null}
                  {onDownloadPdf ? (
                    <Button
                      style={{ flex: 1 }}
                      disabled={downloadingPdf || downloadBlockedReason !== null}
                      title={downloadBlockedReason ?? 'The quotation as the customer receives it'}
                      onClick={onDownloadPdf}
                    >
                      {downloadingPdf ? 'Preparing…' : downloadBlockedReason !== null ? '⛔ Add margin first' : '⬇ PDF'}
                    </Button>
                  ) : null}
                </div>
                {downloadBlockedReason !== null ? (
                  <div style={{ color: 'var(--warn)', fontSize: 12, marginTop: 6 }}>{downloadBlockedReason.replace(/^Can't send: /, '')}</div>
                ) : null}
              </>
            ) : null}
            {onMarkSent && status === 'draft' ? (
              <div style={{ marginTop: 10, fontSize: 12, color: 'var(--muted)', textAlign: 'center' }}>
                Mailed it to the customer yourself?{' '}
                <span
                  className="linkish"
                  role="button"
                  tabIndex={0}
                  onClick={markingSent ? undefined : onMarkSent}
                  onKeyDown={(e) => {
                    if (!markingSent && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault()
                      onMarkSent()
                    }
                  }}
                  title="Record the quotation as sent without emailing it from here — for one mailed from Outlook or handed over in person."
                >
                  {markingSent ? 'Marking as sent…' : 'Mark as sent outside the app'}
                </span>
              </div>
            ) : null}
            {/*
              Shown ABOVE the send, and in warn tone rather than danger: it is not stopping
              anything. The purchaser is about to send a quotation with lines at 0.00 and should
              know which — sending them is legitimate, sending them unnoticed is not.
            */}
            {unpricedWarning ? (
              <div style={{ color: 'var(--warn)', fontSize: 11, marginTop: 8 }}>⚠ {unpricedWarning}</div>
            ) : null}
            {sendBlockedReason !== null ? (
              <div style={{ color: 'var(--warn)', fontSize: 11, marginTop: 6 }}>{sendBlockedReason}</div>
            ) : status !== 'draft' ? (
              <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 6 }}>
                Sent quotations are re-issued as a revision, not re-sent.
              </div>
            ) : null}
        </>
      </Card>
    </div>
  )
}
